import { Filter, Search } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/shared/components/ui/pagination'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { cn } from '@/shared/lib/utils'

function getPageItems(current, totalPages) {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1)
  }

  const pages = new Set([1, totalPages, current, current - 1, current + 1])
  return [...pages]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b)
    .reduce((acc, page, index, list) => {
      if (index > 0 && page - list[index - 1] > 1) {
        acc.push('ellipsis')
      }
      acc.push(page)
      return acc
    }, [])
}

function ShimmerCell({ className }) {
  return (
    <div
      className={cn(
        'h-3.5 animate-pulse rounded-md bg-gradient-to-r from-slate-100 via-slate-200/70 to-slate-100',
        className,
      )}
    />
  )
}

function TableLoadingRows({ columns, rows = 5 }) {
  return Array.from({ length: rows }).map((_, rowIndex) => (
    <TableRow key={`shimmer-row-${rowIndex}`} className="hover:bg-transparent">
      {columns.map((column, colIndex) => (
        <TableCell key={column.id} className={column.className}>
          <ShimmerCell
            className={cn(
              colIndex === 0 ? 'h-9 w-40 max-w-full' : 'w-24 max-w-full',
              colIndex === columns.length - 1 && 'ml-auto w-16',
            )}
          />
        </TableCell>
      ))}
    </TableRow>
  ))
}

function MobileLoadingCards({ rows = 4 }) {
  return (
    <div className="space-y-3 p-3 md:hidden">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={`shimmer-card-${index}`}
          className="rounded-2xl border border-black/[0.06] bg-white p-3.5"
        >
          <div className="flex items-start gap-3">
            <ShimmerCell className="size-10 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <ShimmerCell className="h-3.5 w-36 max-w-full" />
              <ShimmerCell className="h-3 w-48 max-w-full" />
              <ShimmerCell className="mt-2 h-3 w-28 max-w-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export function DataTablePagination({
  page,
  pageSize,
  total,
  onPageChange,
  className,
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const current = Math.min(Math.max(page, 1), totalPages)
  const start = total === 0 ? 0 : (current - 1) * pageSize + 1
  const end = Math.min(current * pageSize, total)
  const items = getPageItems(current, totalPages)

  return (
    <div
      className={cn(
        'flex flex-col gap-3 border-t border-black/[0.05] px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4',
        className,
      )}
    >
      <p className="text-center text-xs font-medium text-muted-foreground sm:text-left">
        Showing <span className="text-foreground">{start}</span>–
        <span className="text-foreground">{end}</span> of{' '}
        <span className="text-foreground">{total}</span>
      </p>

      <Pagination className="mx-0 w-full justify-center sm:w-auto sm:justify-end">
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious
              disabled={current <= 1}
              onClick={() => onPageChange?.(current - 1)}
            />
          </PaginationItem>

          {items.map((item, index) =>
            item === 'ellipsis' ? (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            ) : (
              <PaginationItem key={item}>
                <PaginationLink
                  isActive={item === current}
                  onClick={() => onPageChange?.(item)}
                >
                  {item}
                </PaginationLink>
              </PaginationItem>
            ),
          )}

          <PaginationItem>
            <PaginationNext
              disabled={current >= totalPages}
              onClick={() => onPageChange?.(current + 1)}
            />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}

/**
 * Global reusable data table.
 *
 * columns: [{ id, header, cell?: (row) => node, className?, headerClassName? }]
 * data: array of row objects (must include stable `id` or pass getRowId)
 * mobileCard?: (row, index) => node — card layout below md breakpoint
 * loading / isLoading: show shimmer rows until data arrives
 */
export function DataTable({
  columns = [],
  data = [],
  getRowId = (row, index) => row.id ?? index,
  search,
  filterSummary,
  onFilterClick,
  toolbarExtra,
  pagination,
  emptyMessage = 'No records found.',
  className,
  tableClassName,
  mobileCard,
  loading = false,
  isLoading = false,
  shimmerRows = 5,
}) {
  const showLoading = Boolean(loading || isLoading)
  const hasToolbar = Boolean(search || filterSummary || toolbarExtra)
  const hasMobileCards = typeof mobileCard === 'function'

  return (
    <div
      className={cn(
        'rounded-xl border border-black/[0.06] bg-white shadow-[0_8px_30px_rgba(15,23,42,0.04)] sm:rounded-2xl',
        className,
      )}
      aria-busy={showLoading}
    >
      {hasToolbar ? (
        <div className="flex flex-col gap-3 border-b border-black/[0.05] p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          {search ? (
            <div className="relative w-full min-w-0 sm:max-w-xl">
              <Search className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                name="table-search"
                type="search"
                autoComplete="off"
                value={search.value ?? ''}
                onChange={(event) => search.onChange?.(event.target.value)}
                placeholder={
                  search.placeholder || 'Search by name, company, phone...'
                }
                className="h-11 w-full rounded-full border-transparent bg-muted/70 pl-10 text-sm shadow-none focus-visible:border-input focus-visible:bg-white"
              />
            </div>
          ) : (
            <div />
          )}

          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            {toolbarExtra}
            {filterSummary ? (
              <Button
                type="button"
                variant="outline"
                size="pill"
                onClick={onFilterClick}
                disabled={showLoading}
                className="h-10 w-full gap-2 border-black/[0.08] bg-white text-xs font-semibold text-foreground sm:w-auto"
              >
                <Filter className="size-3.5" />
                {filterSummary}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {hasMobileCards ? (
        showLoading ? (
          <MobileLoadingCards rows={shimmerRows} />
        ) : (
          <div className="space-y-3 p-3 md:hidden">
            {data.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                {emptyMessage}
              </p>
            ) : (
              data.map((row, index) => (
                <div key={getRowId(row, index)}>{mobileCard(row, index)}</div>
              ))
            )}
          </div>
        )
      ) : null}

      <div
        className={cn(
          'datatable-scroll w-full overflow-x-auto overflow-y-hidden',
          hasMobileCards && 'hidden md:block',
        )}
      >
        <Table className={cn('min-w-[1200px] w-full', tableClassName)}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {columns.map((column) => (
                <TableHead
                  key={column.id}
                  className={column.headerClassName}
                  style={column.width ? { width: column.width } : undefined}
                >
                  {typeof column.header === 'function'
                    ? column.header()
                    : column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {showLoading ? (
              <TableLoadingRows columns={columns} rows={shimmerRows} />
            ) : data.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columns.length}
                  className="h-28 text-center text-sm text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow key={getRowId(row, index)}>
                  {columns.map((column) => (
                    <TableCell key={column.id} className={column.className}>
                      {column.cell
                        ? column.cell(row, index)
                        : row[column.accessorKey ?? column.id]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && !showLoading ? (
        <DataTablePagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={pagination.onPageChange}
        />
      ) : null}
    </div>
  )
}
